// One-time-code input (shadcn new-york-v4 `input-otp`, on the `input-otp` package). One real <input>
// sits under the slots, so typing, paste, autofill (`autocomplete="one-time-code"`), backspace and the
// arrow keys all work natively; the slots only draw it. Give the root an `aria-label` (or a <Label
// htmlFor>) since the slots are not labels. Slots share the control look of `input.tsx`.
import { OTPInput, OTPInputContext } from 'input-otp';
import { MinusIcon } from 'lucide-react';
import type * as React from 'react';
import { useContext } from 'react';

import { cn } from '@/lib/utils';

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn('flex items-center gap-2 has-disabled:opacity-50', containerClassName)}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="input-otp-group" className={cn('flex items-center gap-2', className)} {...props} />;
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  index: number;
}) {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        'relative flex size-10 items-center justify-center rounded-control border border-line-control bg-surface font-mono font-semibold text-base text-fg outline-none transition-[border-color,box-shadow] data-[active=true]:z-10 data-[active=true]:border-label data-[active=true]:ring-[3px] data-[active=true]:ring-label/25 aria-invalid:border-err data-[active=true]:aria-invalid:ring-err/20',
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-5 w-px animate-pulse bg-fg" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    // Decorative: the single <input> already tells assistive tech the code's length.
    <div data-slot="input-otp-separator" aria-hidden="true" className="text-fg-muted" {...props}>
      <MinusIcon className="size-4" />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
